import React, { useMemo } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import dayjs from 'dayjs';
import { useAppContext } from 'providers/AppProvider';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import { LineChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { ThemeVariant } from 'config';
import { tooltipFormatterList } from 'helpers/echart-utils';
import { OrderData } from 'api/dashboard/_request';
import isoWeek from 'dayjs/plugin/isoWeek';
dayjs.extend(isoWeek);

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LineChart,
  CanvasRenderer,
  LegendComponent
]);

type RangeType = 'today' | 'week' | 'month' | 'year' | 'custom';

type EcomTotalOrderChatProps = {
  data: OrderData[];
  range: RangeType;
};

const generateDateRange = (
  range: RangeType,
  customStartDate?: string | null,
  customEndDate?: string | null
): string[] => {
  const now = dayjs();

  if (range === 'custom' && (!customStartDate || !customEndDate)) {
    // fallback to year if custom dates missing
    range = 'year';
  }

  switch (range) {
    case 'today':
      return Array.from(
        { length: 24 },
        (_, i) => `${String(i).padStart(2, '0')}:00`
      );
    case 'week': {
      const start = now.startOf('week');
      return Array.from({ length: 7 }, (_, i) =>
        start.add(i, 'day').format('DD-MMM')
      );
    }
    case 'month': {
      const start = now.startOf('month');
      const end = now.endOf('month');
      const days = [];
      for (
        let date = start;
        date.isBefore(end) || date.isSame(end, 'day');
        date = date.add(3, 'day')
      ) {
        days.push(date.format('DD-MMM'));
      }
      return days;
    }
    case 'year':
      return Array.from({ length: 12 }, (_, i) =>
        dayjs().month(i).format('MMM')
      );
    case 'custom': {
      // use params passed or fallback to URL search
      const start =
        customStartDate ??
        new URLSearchParams(window.location.search).get('startDate');
      const end =
        customEndDate ??
        new URLSearchParams(window.location.search).get('endDate');

      if (!start || !end) {
        return Array.from({ length: 12 }, (_, i) =>
          dayjs().month(i).format('MMM')
        );
      }

      const startDate = dayjs(start);
      const endDate = dayjs(end);
      const dates = new Set<string>();
      for (
        let d = startDate;
        d.isBefore(endDate) || d.isSame(endDate, 'day');
        d = d.add(1, 'day')
      ) {
        dates.add(d.format('MMM-YYYY'));
      }
      return Array.from(dates);
    }
    default:
      return [];
  }
};

const getDefaultOptions = (
  theme: ThemeVariant,
  getThemeColor: (name: string) => string,
  dates: string[],
  values: number[],
  range: RangeType
) => ({
  color: [getThemeColor('primary'), getThemeColor('info')],
  tooltip: {
    trigger: 'axis',
    padding: 10,
    backgroundColor: getThemeColor('body-highlight-bg'),
    borderColor: getThemeColor('border-color'),
    textStyle: { color: getThemeColor('light-text-emphasis') },
    borderWidth: 1,
    transitionDuration: 0,
    axisPointer: { type: 'none' },
    formatter: tooltipFormatterList
  },
  xAxis: [
    {
      type: 'category',
      data: dates,
      axisLabel: {
        formatter: (value: string) => value,
        interval: 'auto',
        hideOverlap: true,
        overflow: 'truncate',
        showMinLabel: true,
        showMaxLabel: true,
        color: getThemeColor('secondary-color'),
        align: 'left',
        fontFamily: 'Nunito Sans',
        fontWeight: 600,
        fontSize: 12,
        padding: [0, 5, 0, 0]
      },
      axisLine: {
        show: true,
        lineStyle: { color: getThemeColor('secondary-bg') }
      },
      axisTick: { show: false },
      splitLine: {
        show: true,
        lineStyle: {
          color:
            theme === 'dark'
              ? getThemeColor('body-highlight-bg')
              : getThemeColor('secondary-bg')
        }
      },
      boundaryGap: range === 'year' ? true : false
    }
  ],
  yAxis: {
    position: 'right',
    axisPointer: { type: 'none' },
    axisTick: 'none',
    splitLine: { show: false },
    axisLine: { show: false },
    axisLabel: { show: false },
    min: 0,
    max: (value: { max: number }) => value.max * 1.2 || 10 // 20% margin top or default 10 if max=0
  },
  series: [
    {
      type: 'line',
      data: values,
      showSymbol: false,
      symbol: 'circle'
    }
  ],
  grid: {
    right: 2,
    left: 5,
    bottom: '30px',
    top: '10%', // top margin to prevent overlap with line
    containLabel: true
  },
  animation: false
});

const EcomTotalOrderChat = ({ data, range }: EcomTotalOrderChatProps) => {
  const {
    config: { theme },
    getThemeColor
  } = useAppContext();

  // Read custom start/end dates from URL params
  const urlParams = new URLSearchParams(window.location.search);
  const customStart = urlParams.get('startDate');
  const customEnd = urlParams.get('endDate');

  // If custom dates missing, fallback to year
  const actualRange: RangeType =
    range === 'custom' && (!customStart || !customEnd) ? 'year' : range;

  const { dates, values } = useMemo(() => {
    // Pass the actual custom dates explicitly to generateDateRange
    const labels = generateDateRange(actualRange, customStart, customEnd);
    const countMap: Record<string, number> = Object.fromEntries(
      labels.map(label => [label, 0])
    );

    data.forEach(order => {
      const date = dayjs(order.createdAt);
      let key = '';

      switch (actualRange) {
        case 'today':
          key = date.format('HH:00');
          break;
        case 'week':
        case 'month':
          key = date.format('DD-MMM');
          break;
        case 'year':
          key = date.format('MMM');
          break;
        case 'custom':
          key = date.format('MMM-YYYY');
          break;
      }

      if (countMap[key] !== undefined) {
        countMap[key] += 1;
      }
    });

    return {
      dates: Object.keys(countMap),
      values: Object.values(countMap)
    };
  }, [data, actualRange, customStart, customEnd]);

  const options = getDefaultOptions(
    theme,
    getThemeColor,
    dates,
    values,
    actualRange
  );

  return (
    <div style={{ height: '100%', width: '100%' }}>
      <ReactEChartsCore
        echarts={echarts}
        option={options}
        style={{ height: '100%', width: '100%' }}
      />
    </div>
  );
};

export default EcomTotalOrderChat;
